import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	TextField,
	Chip,
	Typography,
	Autocomplete,
} from "@mui/material";

function BarcodeInputDialog({ open, onClose, onSave, productName, existingBarcodes = [] }) {
	const [barcodes, setBarcodes] = useState(existingBarcodes);

	useEffect(() => {
		if (open) {
			setBarcodes(existingBarcodes || []);
		}
	}, [open, existingBarcodes]);

	const handlePaste = (e) => {
		e.preventDefault();
		const paste = e.clipboardData.getData("text");
		if (!paste) return;

		const newItems = paste
			.split(/[\n\r\s,;]+/)
			.map((item) => item.trim())
			.filter((item) => item !== "");

		const uniqueNewItems = [...new Set(newItems)].filter((item) => !barcodes.includes(item));

		if (uniqueNewItems.length > 0) {
			setBarcodes([...barcodes, ...uniqueNewItems]);
		}
	};

	const handleInputChange = (event, newValue, reason) => {
		if (reason === 'createOption') {
			const lastValue = newValue[newValue.length - 1];
			if (lastValue) {
				const splitValues = lastValue
					.split(/[,\s;]+/)
					.map((item) => item.trim())
					.filter((item) => item !== "");
				
				const allValues = [...newValue.slice(0, -1), ...splitValues];
				setBarcodes([...new Set(allValues)]);
				return;
			}
		}
		setBarcodes([...new Set(newValue)]);
	};

	const handleSave = () => {
		onSave(barcodes);
		setBarcodes([]);
		onClose();
	};

	const handleClose = () => {
		setBarcodes([]);
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				Add Barcodes for {productName}
			</DialogTitle>
			<DialogContent>
				<Box mt={2}>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						Enter or paste multiple barcodes (separated by comma, space, or new line)
					</Typography>
					<Box
						sx={{
							border: "1px solid #c4c4c4",
							borderRadius: "4px",
							maxHeight: "300px",
							overflow: "hidden",
							p: 1,
							overflowY: "auto",
							mt: 2,
						}}
					>
						<Autocomplete
							sx={{
								"& .MuiAutocomplete-inputRoot": {
									display: "flex",
									flexWrap: "wrap",
									alignItems: "flex-start",
									gap: "4px",
									maxHeight: "100%",
									overflowY: "auto",
									overflowX: "hidden",
								},
								"& .MuiAutocomplete-tag": {
									flexShrink: 0,
									maxWidth: "100%",
								},
								"& .MuiAutocomplete-input": {
									flexGrow: 1,
									minWidth: "120px",
								},
							}}
							multiple
							freeSolo
							options={[]}
							value={barcodes}
							onChange={handleInputChange}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip
										variant="outlined"
										label={option}
										{...getTagProps({ index })}
										size="small"
									/>
								))
							}
							renderInput={(params) => (
								<TextField
									{...params}
									variant="standard"
									placeholder="Enter or Paste barcodes"
									onPaste={handlePaste}
									InputProps={{
										...params.InputProps,
										disableUnderline: true,
									}}
								/>
							)}
						/>
					</Box>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
						Total Barcodes: {barcodes.length}
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="inherit">
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained" disabled={barcodes.length === 0}>
					Save Barcodes
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default BarcodeInputDialog;
